import { forwardRef, PropsWithChildren, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useCustomTasksApi } from "../../http/customTasks/api";
import { useDefaultTaskCategoriesApi } from "../../http/defaultTaskCategories/api";
import { DefaultTask } from "../../http/defaultTasks/types";
import { useSubsidiariesApi } from "../../http/subsidiaries/api";
import SvgAdd from "../icons/Add";
import SvgArrowDropDown from "../icons/ArrowDropDown";
import SvgDelete from "../icons/Delete";
import SvgEdit from "../icons/Edit";
import { InfoBox, WarningBox } from "../ui/Boxes";
import Button from "../ui/Button";
import LoadingSpinner from "../ui/LoadingSpinner";
import CreateSubsidiaryCustomTaskDialog from "./CreateSubsidiaryCustomTaskDialog";
import DeleteSubsidiaryCustomTaskDialog from "./DeleteSubsidiaryCustomTaskDialog";
import EditSubsidiaryCustomTasks from "./EditSubsidiaryCustomTasks";
import styles from "./SubsidiaryCustomTasks.module.css";

export type SubsidiaryCustomTasksHandle = {
  notifyEdit: () => void;
};

type SubsidiaryTaskCategory = {
  id: string;
  tasks: DefaultTask[];
  isCustom: boolean;
};

const order = ["new-car", "new-driver", "close-driver"];

const SubsidiaryCustomTasks = forwardRef<SubsidiaryCustomTasksHandle, PropsWithChildren>(({}, ref) => {
  const aborter = useRef(new AbortController());
  const i18n = useI18n();
  const { id } = useParams<{ id: string }>();
  const taskCategoriesApi = useDefaultTaskCategoriesApi();
  const subsidiariesApi = useSubsidiariesApi();
  const customTasksApi = useCustomTasksApi();

  const [tasks, setTasks] = useState<SubsidiaryTaskCategory[]>([]);
  const [categoryToCreate, setCategoryToCreate] = useState<SubsidiaryTaskCategory>();
  const [categoryToEdit, setCategoryToEdit] = useState<SubsidiaryTaskCategory>();
  const [categoryToDelete, setCategoryToDelete] = useState<SubsidiaryTaskCategory>();

  const subsidiary = subsidiariesApi.useAdminSubsidiary(id!, aborter.current.signal);
  const taskCategoriesReslt = taskCategoriesApi.useAdminDefaultTaskCategories(aborter.current.signal);
  const customTaskReslt = customTasksApi.useAdminSubsidiaryCustomTasks(id!, aborter.current.signal);

  const [customTasks, customTaskError] = useDataInitializer(customTaskReslt.data, customTaskReslt.error);

  // useEffect(() => {
  //   id && customTasksSwr.trigger();
  // }, [id]);

  useImperativeHandle(ref, () => ({
    notifyEdit() {
      // id && customTasksSwr.trigger();
      subsidiary.mutate();
    },
  }));

  useEffect(() => {
    if (taskCategoriesReslt.data && customTaskReslt.data) {
      const { tasks: customTask, ids } = customTaskReslt.data.reduce(
        (
          rv: {
            tasks: SubsidiaryTaskCategory[];
            ids: string[];
          },
          x
        ) => {
          rv.tasks.push({
            id: x.id,
            isCustom: true,
            tasks: x.tasks,
          });
          rv.ids.push(x.id);
          return rv;
        },
        { tasks: [], ids: [] }
      );

      const defaultTasks: SubsidiaryTaskCategory[] =
        taskCategoriesReslt.data
          .filter((t) => !ids.includes(t.id))
          .map((x) => ({
            id: x.id,
            isCustom: false,
            tasks: x.tasks ?? [],
          })) ?? [];

      setTasks([...customTask, ...defaultTasks]);
    }
  }, [taskCategoriesReslt.data, customTaskReslt.data]);

  if (!taskCategoriesReslt.data && !taskCategoriesReslt.error && !customTaskError) {
    return (
      <div className={styles["loading-spinner"]}>
        <LoadingSpinner />
      </div>
    );
  }

  const hasCustomTasks = !!customTasks?.length;

  const sortedTasks = [...tasks].sort((a, b) => {
    const stripCountry = (id: string) => id.replace(/-[A-Za-z]{2}$/, ""); // removes trailing -xx if present

    const baseA = stripCountry(a.id);
    const baseB = stripCountry(b.id);

    const rankA = order.indexOf(baseA);
    const rankB = order.indexOf(baseB);
    if (rankA !== rankB) return (rankA === -1 ? Infinity : rankA) - (rankB === -1 ? Infinity : rankB);

    const hasSuffixA = a.id !== baseA;
    const hasSuffixB = b.id !== baseB;
    if (hasSuffixA !== hasSuffixB) return hasSuffixA ? 1 : -1;

    if (hasSuffixA) {
      // compare by country code
      const suffixA = a.id.split("-").pop()!;
      const suffixB = b.id.split("-").pop()!;
      return suffixA.localeCompare(suffixB);
    }

    return 0; // same base, both plain
  });

  return (
    <>
      <div className={styles["box-contaner"]}>
        {hasCustomTasks ? (
          <WarningBox text={i18n.t.common.subsidiaryHasCustomTasks} />
        ) : (
          <InfoBox text={i18n.t.common.subsidiaryUsesDefaultTasks} />
        )}
      </div>
      <ul className={styles.categories}>
        {sortedTasks.map((c) => (
          <li key={`c_${c.id}`}>
            <div className={styles["category-row"]}>
              <div className={styles["row-elements-container"]}>
                <SvgArrowDropDown className={styles.icon} />
                <b>
                  <div>{c.id}</div>
                </b>
              </div>
              {customTasks?.some((x) => x.id === c.id) ? (
                <div className={`${styles["row-elements-container"]} ${styles["buttons-container"]}`}>
                  <Button
                    buttonProps={{
                      onClick: () => setCategoryToEdit(c),
                    }}
                    glyph={SvgEdit}
                  />
                  <Button
                    buttonProps={{
                      onClick: () => setCategoryToDelete(c),
                    }}
                    type="secondary"
                    glyph={SvgDelete}
                  />
                </div>
              ) : (
                <div className={`${styles["row-elements-container"]} ${styles["buttons-container"]}`}>
                  <Button
                    buttonProps={{
                      onClick: () => setCategoryToCreate(c),
                    }}
                    glyph={SvgAdd}
                  />
                </div>
              )}
            </div>
            {c.tasks?.length ? (
              <ul className={styles.tasks}>
                {c.tasks
                  .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                  .map((t) => (
                    <li key={`t_${t.id}`}>
                      <div className={styles.task}>{t.name}</div>
                    </li>
                  ))}
              </ul>
            ) : undefined}
          </li>
        ))}
      </ul>

      {!!id && !!categoryToDelete && (
        <DeleteSubsidiaryCustomTaskDialog
          defaultTaskCategory={categoryToDelete.id}
          subsidiaryId={id}
          onClose={() => setCategoryToDelete(undefined)}
        />
      )}

      {!!id && !!categoryToEdit && (
        <EditSubsidiaryCustomTasks
          defaultTaskCategory={categoryToEdit}
          subsidiaryId={id}
          onClose={() => setCategoryToEdit(undefined)}
        />
      )}

      {!!id && !!categoryToCreate && (
        <CreateSubsidiaryCustomTaskDialog
          defaultTaskCategory={categoryToCreate.id}
          subsidiaryId={id}
          onClose={() => setCategoryToCreate(undefined)}
        />
      )}
    </>
  );
});

export default SubsidiaryCustomTasks;
